import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import MetricsCard from '../../components/Shared/MetricsCard';
import DataTable from '../../components/Shared/DataTable';

/**
 * SchoolDetailPage - Detailed view of a specific school
 * Shows school info, administrators, teachers, students, and analytics
 */
const SchoolDetailPage = () => {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    const fetchSchoolDetail = async () => {
      try {
        setLoading(true);
        const data = await apiRequest(`/api/super-admin/schools/${schoolId}`, 'GET');
        setSchoolData(data);
      } catch (err) {
        console.error('Failed to load school details:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSchoolDetail();
  }, [schoolId]);

  const handleRegenerateCode = async () => {
    if (!window.confirm('Are you sure you want to generate a new master activation code? The old code will remain valid.')) {
      return;
    }

    setRegenerating(true);
    try {
      const response = await apiRequest(
        `/api/super-admin/schools/${schoolId}/master-code`,
        'POST',
        { notes: 'Regenerated from super admin dashboard' }
      );

      alert(`New master code generated: ${response.code}\n\nNote: The old code is still valid.`);

      // Refresh school data to show the new code
      const data = await apiRequest(`/api/super-admin/schools/${schoolId}`, 'GET');
      setSchoolData(data);
    } catch (err) {
      alert(`Failed to regenerate code: ${err.message}`);
    } finally {
      setRegenerating(false);
    }
  };

  const navItems = [
    { label: 'Dashboard', path: '/super-admin/dashboard', icon: '📊' },
    { label: 'Schools', path: '/super-admin/schools', icon: '🏫' },
    { label: 'Platform Analytics', path: '/super-admin/analytics', icon: '📈' },
    { label: 'Activation Codes', path: '/super-admin/codes', icon: '🔑' },
    { label: 'Settings', path: '/super-admin/settings', icon: '⚙️' }
  ];

  // Administrators table columns
  const adminColumns = [
    {
      key: 'full_name',
      label: 'Name',
      sortable: true,
      render: (admin) => <span className="font-medium text-gray-900">{admin.full_name}</span>
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (admin) => <span className="text-gray-600">{admin.email}</span>
    },
    {
      key: 'job_title',
      label: 'Job Title',
      sortable: true,
      render: (admin) => <span className="text-gray-600">{admin.job_title || 'N/A'}</span>
    },
    {
      key: 'created_at',
      label: 'Joined',
      sortable: true,
      render: (admin) => (
        <span className="text-gray-600 text-sm">
          {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : 'N/A'}
        </span>
      )
    }
  ];

  // Teachers table columns
  const teacherColumns = [
    {
      key: 'full_name',
      label: 'Name',
      sortable: true,
      render: (teacher) => <span className="font-medium text-gray-900">{teacher.full_name}</span>
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (teacher) => <span className="text-gray-600">{teacher.email}</span>
    },
    {
      key: 'grade_level',
      label: 'Grade',
      sortable: true,
      render: (teacher) => <span className="text-gray-600">{teacher.grade_level || 'N/A'}</span>
    },
    {
      key: 'subject',
      label: 'Subject',
      sortable: true,
      render: (teacher) => <span className="text-gray-600">{teacher.subject || 'N/A'}</span>
    },
    {
      key: 'student_count',
      label: 'Students',
      sortable: true,
      render: (teacher) => <span className="font-medium text-indigo-600">{teacher.student_count || 0}</span>
    }
  ];

  if (loading) {
    return (
      <DashboardLayout
        title="School Details"
        navItems={navItems}
        userRole={currentUser?.user_type || 'super-admin'}
        userName={currentUser?.email || 'Admin'}
      >
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600">Loading school details...</span>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout
        title="School Details"
        navItems={navItems}
        userRole={currentUser?.user_type || 'super-admin'}
        userName={currentUser?.email || 'Admin'}
      >
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Error loading school details: {error}
        </div>
        <button
          onClick={() => navigate('/super-admin/dashboard')}
          className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          ← Back to Dashboard
        </button>
      </DashboardLayout>
    );
  }

  const school = schoolData?.school;
  const administrators = schoolData?.administrators || [];
  const teachers = schoolData?.teachers || [];
  const students = schoolData?.students || { total: 0, active: 0 };

  // Calculate license usage
  const licenseUsagePercent = school?.license_count
    ? Math.round((school.licenses_used / school.license_count) * 100)
    : 0;

  return (
    <DashboardLayout
      title="School Details"
      navItems={navItems}
      userRole={currentUser?.user_type || 'super-admin'}
      userName={currentUser?.email || 'Admin'}
    >
      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/super-admin/dashboard')}
          className="text-purple-600 hover:text-purple-700 font-medium flex items-center"
        >
          ← Back to All Schools
        </button>
      </div>

      {/* School Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">{school?.school_name}</h2>
            <p className="text-gray-600 mt-1">{school?.district_name || 'No district'}</p>
            <p className="text-gray-600 text-sm mt-1">
              {school?.city && school?.state ? `${school.city}, ${school.state}` : 'Location not specified'}
            </p>
          </div>
          <div className="text-right">
            <span
              className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                school?.subscription_status === 'active'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {school?.subscription_status || 'Unknown'}
            </span>
            {school?.subscription_end_date && (
              <p className="text-gray-600 text-sm mt-2">
                Expires: {new Date(school.subscription_end_date).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        {/* Master Activation Code */}
        {school?.master_activation_code && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-purple-900 mb-1">Master Activation Code</p>
                  <p className="font-mono text-2xl text-purple-700 font-bold">
                    {school.master_activation_code}
                  </p>
                  {school.activation_code_activated_at && (
                    <p className="text-xs text-purple-600 mt-1">
                      Activated: {new Date(school.activation_code_activated_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(school.master_activation_code);
                      alert('Activation code copied to clipboard!');
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
                  >
                    📋 Copy Code
                  </button>
                  <button
                    onClick={handleRegenerateCode}
                    disabled={regenerating}
                    className="px-4 py-2 bg-white text-purple-600 border-2 border-purple-600 rounded-lg hover:bg-purple-50 font-medium disabled:opacity-50"
                  >
                    {regenerating ? '🔄 Generating...' : '🔄 New Code'}
                  </button>
                </div>
              </div>
              <p className="text-xs text-purple-800 mt-3 bg-purple-100 p-2 rounded">
                💡 Share this code with the school administrator to register their account at the School Admin Registration page.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* School Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricsCard
          title="Total Students"
          value={students.total || 0}
          subtitle={`${students.active || 0} active`}
          icon="👥"
        />
        <MetricsCard
          title="Total Teachers"
          value={teachers.length}
          subtitle="on staff"
          icon="👨‍🏫"
        />
        <MetricsCard
          title="License Usage"
          value={`${licenseUsagePercent}%`}
          subtitle={`${school?.licenses_used || 0} of ${school?.license_count || 0}`}
          trendColor={licenseUsagePercent >= 90 ? 'red' : licenseUsagePercent >= 75 ? 'yellow' : 'green'}
          icon="📊"
        />
        <MetricsCard
          title="Administrators"
          value={administrators.length}
          subtitle="managing school"
          icon="🔑"
        />
      </div>

      {/* School Administrators */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">School Administrators</h3>
        <DataTable
          columns={adminColumns}
          data={administrators}
          emptyMessage="No administrators found for this school"
        />
      </div>

      {/* Teachers */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Teachers</h3>
        <DataTable
          columns={teacherColumns}
          data={teachers}
          emptyMessage="No teachers found for this school"
        />
      </div>

      {/* Subscription Details */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Details</h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">License Count</p>
            <p className="text-2xl font-bold text-gray-900">{school?.license_count || 0}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Licenses Used</p>
            <p className="text-2xl font-bold text-indigo-600">{school?.licenses_used || 0}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Subscription Start</p>
            <p className="text-lg font-medium text-gray-900">
              {school?.subscription_start_date
                ? new Date(school.subscription_start_date).toLocaleDateString()
                : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Subscription End</p>
            <p className="text-lg font-medium text-gray-900">
              {school?.subscription_end_date
                ? new Date(school.subscription_end_date).toLocaleDateString()
                : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SchoolDetailPage;
